import { useCallback, useLayoutEffect, useRef } from 'react';

// Unhurried, and eased at both ends: the card is lifted out of the fan and
// set down, not thrown. It must have landed before the fan starts to fold
// (FAN_ROLL_MS after the last pick), which moves the place it is landing in.
const FLIGHT_MS = 950;
const GLIDE = 'cubic-bezier(0.45, 0, 0.2, 1)';
// The card reaches its full size a little ahead of its place
const GROW = 'cubic-bezier(0.3, 0, 0.3, 1)';
// How far, at most, the card leans into its direction of travel
const MAX_LEAN = 5;

const centre = (rect) => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });

/**
 * Carries each picked card from the fan to its place in the spread.
 *
 * The card is laid in the spread at once (it is the one that gets turned
 * and read); this starts it over the fan card it was picked as and lets it
 * travel home, so the pick is seen to arrive rather than appear.
 *
 * `count` is how many cards the spread holds. Put `landingRef` on the
 * element the cards are laid in, and call `launchFrom(rect)` with the fan
 * card's place just before the pick is made.
 */
export function useCardFlight(count) {
  const landingRef = useRef(null);
  const from = useRef(null);

  const launchFrom = useCallback((rect) => {
    from.current = rect;
  }, []);

  useLayoutEffect(() => {
    const start = from.current;
    from.current = null;
    if (!start || !count) return;

    // The cards are laid in pick order, so the newest is the last
    const card = landingRef.current?.querySelectorAll('.card-hover-wrapper')[count - 1];
    if (!card?.animate) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const end = card.getBoundingClientRect();
    if (!end.width) return;
    const a = centre(start);
    const b = centre(end);

    // Raised over the fan it is leaving for as long as it is in the air
    const place = card.closest('.card-position, .card-container');
    place?.classList.add('is-landing');

    // Position, size and lean each run on their own curve; as one transform
    // they would have to share one, and the flight would read as a slide
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const lean = Math.max(-MAX_LEAN, Math.min(MAX_LEAN, -dx / 60));
    const timing = { duration: FLIGHT_MS };
    const flight = card.animate(
      { translate: [`${dx}px ${dy}px`, '0px 0px'] },
      { ...timing, easing: GLIDE }
    );
    card.animate({ scale: [start.width / end.width, 1] }, { ...timing, easing: GROW });
    card.animate(
      { rotate: ['0deg', `${lean}deg`, '0deg'] },
      { ...timing, easing: 'ease-in-out' }
    );

    const land = () => place?.classList.remove('is-landing');
    flight.onfinish = land;
    flight.oncancel = land;
  }, [count]);

  return { landingRef, launchFrom };
}
